/**
 * A BEÍRHATÓ IDŐSZAK (#74) — „Ettől–Eddig", ahogy a DOS-ban (Viktor, 10-23).
 *
 * A könyvelő nem pipál hónapokat, hanem BEÍR: `2026-08`, `08`, `aug`, vagy
 * `-3` („három hónapja"). A mező alatt kiírjuk, mit értettünk belőle — így az
 * elgépelés azonnal látszik, nem egy üres listából derül ki.
 *
 * Tiszta függvény: a „ma" bemenet, nem a rendszeróra — a teszt nem öregszik
 * (`pontkft-teszt-fali-idopont-bomba`).
 */

import { fold } from '@/utils/fold';

/** `YYYY-MM`, vagy `null` = üres mező (nyitott határ), vagy `'invalid'`. */
export type ParsedPeriod = string | null | 'invalid';

/** A hónapok rövid neve — ékezet nélkül is (`marc`, `apr`). */
const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'maj',
  'jun',
  'jul',
  'aug',
  'szep',
  'okt',
  'nov',
  'dec',
];

function format(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/** `ma` hónapja `delta` hónappal eltolva. */
function shift(today: Date, delta: number): string {
  const date = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + delta, 1));
  return format(date.getUTCFullYear(), date.getUTCMonth() + 1);
}

function monthOnly(text: string): number | null {
  if (/^\d{1,2}$/.test(text)) {
    const month = Number(text);
    return month >= 1 && month <= 12 ? month : null;
  }
  // Legalább három betű: a „ma" lehetne március és május is.
  if (text.length < 3) {
    return null;
  }
  const at = MONTHS.findIndex((name) => name.startsWith(text) || text.startsWith(name));
  return at === -1 ? null : at + 1;
}

/**
 * A mező szövege → időszak.
 *
 * - `2026-08`, `2026.08`, `2026.8.` → `2026-08`
 * - `08`, `8`, `aug`, `augusztus` → az IDEI év adott hónapja
 * - `-3` → a mai hónap előtti harmadik (a „-0" a mostani)
 * - üres → `null` (nyitott határ)
 */
export function parsePeriodInput(raw: string, today: Date): ParsedPeriod {
  const text = fold(raw.trim()).replace(/\.$/, '');
  if (text === '') {
    return null;
  }

  const relative = /^-(\d{1,2})$/.exec(text);
  if (relative) {
    return shift(today, -Number(relative[1]));
  }

  const full = /^(\d{4})[-.]\s?(\d{1,2})$/.exec(text);
  if (full) {
    const month = Number(full[2]);
    return month >= 1 && month <= 12 ? format(Number(full[1]), month) : 'invalid';
  }

  const month = monthOnly(text);
  return month === null ? 'invalid' : format(today.getUTCFullYear(), month);
}

/** `2026-08` → „2026. aug." — a mező alatti visszaigazolás. */
export function periodLabel(period: string): string {
  const [year, month] = period.split('-').map(Number);
  return `${year}. ${MONTH_LABELS[month - 1]}`;
}

const MONTH_LABELS = [
  'jan.',
  'febr.',
  'márc.',
  'ápr.',
  'máj.',
  'jún.',
  'júl.',
  'aug.',
  'szept.',
  'okt.',
  'nov.',
  'dec.',
];

/** A csoportfejléc: `2026-08` → „2026. augusztus". */
export function periodHeading(period: string): string {
  const [year, month] = period.split('-').map(Number);
  return `${year}. ${MONTH_NAMES[month - 1]}`;
}

const MONTH_NAMES = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];
